import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { Button, Typography } from '@mui/material'
import { OverlayState } from '@/hooks/useOverlay'
import { Dialog, DialogContent, DialogFooter } from '@/components/dialog'
import { FormTextField, PixyDocsForm } from '@/components/forms'

export type AddCardNumberDialogProps = {
  overlay: OverlayState
  existingCardNumbers: string[]
  onSave: (lastFour: string) => void
}

type FormValues = {
  lastFour: string
}

export default function AddCardNumberDialog({
  overlay,
  existingCardNumbers,
  onSave,
}: AddCardNumberDialogProps) {
  const defaultValues = useMemo<FormValues>(() => ({ lastFour: '' }), [])
  const methods = useForm<FormValues>({
    defaultValues,
    mode: 'onChange',
  })
  const { reset } = methods

  function handleSubmit(values: FormValues) {
    onSave(values.lastFour)
    overlay.close()
  }

  useEffect(() => {
    if (overlay.isOpen) {
      reset(defaultValues)
    }
  }, [defaultValues, overlay.isOpen, reset])

  const helperText =
    'Please enter the last 4 digits of your card. No other card information is stored.'

  const { formState } = methods
  const { isValid } = formState

  const error = formState.errors.lastFour

  return (
    <Dialog title="Enter Card Last 4 Digits" {...overlay} maxWidth="xs">
      <PixyDocsForm methods={methods} onSubmit={handleSubmit}>
        <DialogContent>
          {/* Uses a textfield instead of FormIntegerField so it can have leading zeros */}
          <FormTextField
            autoFocus
            fullWidth
            label="Last 4 Digits"
            name="lastFour"
            helperText={error ? error.message : helperText}
            error={!!error}
            color={isValid ? 'success' : undefined}
            required
            slotProps={{
              htmlInput: {
                maxLength: 4,
              },
              input: {
                startAdornment: (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    flexShrink="0"
                    component="span"
                    sx={{ pt: '14px', fontSize: '14px', opacity: 0.5 }}
                  >
                    XXXX-XXXX-XXXX-
                  </Typography>
                ),
              },
            }}
            useControllerProps={{
              rules: {
                validate: (value: string) => {
                  const value_str = `${value}`
                  if (existingCardNumbers.includes(value_str)) {
                    return 'This card number has already been added.'
                  } else if (isNaN(Number(value_str))) {
                    return 'Please enter a valid card number.'
                  } else if (value_str.length < 4) {
                    return helperText
                  }
                  return true
                },
              },
            }}
          />
        </DialogContent>
        <DialogFooter>
          <Button variant="text" onClick={overlay.close}>
            Cancel
          </Button>
          <Button type="submit" disabled={!isValid}>
            Save
          </Button>
        </DialogFooter>
      </PixyDocsForm>
    </Dialog>
  )
}
